import { Injectable } from '@angular/core';
import { State, Selector, StateContext, Action } from '@ngxs/store';
import dayjs from 'dayjs';
import { ResetShop, SetShoppingCartContent, ShopStateModel } from './models/shop.state.model';
import { StateNames } from './models/state-names.enum';

const defaultShopState = {
  type: 'graafmelding' as const,
  activities: [],
  startDate: dayjs().add(3, 'days').toDate().toString(),
  endDate: dayjs().add(10, 'days').toDate().toString(),
  executionDate: dayjs().toDate().toString(),
  geometries: [],
  informationPolygonGeometries: [],
  addedOn: new Date().toString(),
  closestAddresses: [],
  selectHouseConnections: false,
  selectedHouseConnections: [],
  notitie: '',
  reference: '',
  projectId: null,
};

@State<ShopStateModel>({
  name: StateNames.shop_v3,
  defaults: defaultShopState,
})
@Injectable()
export class ShopState {
  @Selector([ShopState])
  static type(state: ShopStateModel) {
    return state.type;
  }

  @Selector([ShopState])
  static activities(state: ShopStateModel) {
    return state.activities;
  }

  @Selector([ShopState])
  static reference(state: ShopStateModel) {
    return state.reference;
  }

  @Selector([ShopState])
  static notitie(state: ShopStateModel) {
    return state.notitie;
  }

  @Selector([ShopState])
  static startDate(state: ShopStateModel) {
    return state.startDate;
  }

  @Selector([ShopState])
  static endDate(state: ShopStateModel) {
    return state.endDate;
  }

  @Selector([ShopState])
  static executionDate(state: ShopStateModel) {
    return state.executionDate;
  }

  @Selector([ShopState])
  static geometries(state: ShopStateModel) {
    return state.geometries;
  }

  @Selector([ShopState])
  static informationPolygonGeometries(state: ShopStateModel) {
    return state.informationPolygonGeometries;
  }

  @Selector([ShopState])
  static closestAddresses(state: ShopStateModel) {
    return state.closestAddresses;
  }

  @Selector([ShopState])
  static selectHouseConnections(state: ShopStateModel) {
    return state.selectHouseConnections;
  }

  @Selector([ShopState])
  static selectedHouseConnections(state: ShopStateModel) {
    return state.selectedHouseConnections;
  }

  @Selector([ShopState])
  static projectId(state: ShopStateModel) {
    return state.projectId;
  }

  @Selector([ShopState])
  static shouldReset(state: ShopStateModel) {
    return dayjs(state.addedOn).diff(dayjs(), 'hours') > 12;
  }

  @Selector([ShopState])
  static orderItems(state: ShopStateModel) {
    return state.geometries.map((geometry, index) => {
      return {
        type: state.type,
        activities: state.activities,
        startDate: state.startDate,
        endDate: state.endDate,
        geometry: geometry,
        informationPolygonGeometry:
          state.informationPolygonGeometries.length > 0 ? state.informationPolygonGeometries[0] : null,
        locatieWerkzaamheden: state.closestAddresses[index],
        notitie: state.notitie,
        reference: state.reference,
        huisaansluitingAdressen: state.selectedHouseConnections,
      };
    });
  }

  constructor() {}

  @Action(SetShoppingCartContent)
  setShoppingCartState(ctx: StateContext<ShopStateModel>, action: SetShoppingCartContent) {
    ctx.patchState(action.payload);
  }

  @Action(ResetShop)
  resetShop(ctx: StateContext<ShopStateModel>, action: ResetShop) {
    if (action.payload.remember_klic_order_details) {
      const activities = ctx.getState().activities;
      const reference = ctx.getState().reference;
      const notitie = ctx.getState().notitie;
      const type = ctx.getState().type;

      ctx.setState({
        ...defaultShopState,
        type: type,
        activities: activities,
        reference: reference,
        notitie: notitie,
      });
    } else {
      ctx.setState(defaultShopState);
    }
  }
}
